import { useState, useEffect } from 'react';
import { Modal, Input, InputGroup, Button, Form, Message, Loader, useToaster } from 'rsuite';
import { useAuth } from '../utils/useAuth';
import { getApiUrl, getAuthHeaders } from '../utils/apiConfig';
import CopyButton from './CopyButton';

function TokenDialog({ show, onClose }) {
  const auth = useAuth();
  const toaster = useToaster();
  const [token, setToken] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);

  // Fetch current token when dialog opens
  useEffect(() => {
    if (show && auth.hasAuth0 && auth.isAuthenticated) {
      fetchCurrentToken();
    }
  }, [show]);

  const fetchCurrentToken = async () => {
    setFetching(true);
    setError('');
    
    try {
      const headers = await getAuthHeaders(auth.getAccessTokenSilently);
      
      const response = await fetch(getApiUrl('/api/users/me/api-key'), {
        method: 'GET',
        headers: headers
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail?.message || data.detail || 'Failed to fetch metadata');
      }

      // Set current token if available
      const metadata = data.metadata || {};
      setToken(metadata.ac_api_key || metadata.AC_API_KEY || '');
    } catch (err) {
      console.error('Failed to fetch current token:', err);
      // Don't show error on fetch, just start with empty token
      setToken('');
    } finally {
      setFetching(false);
    }
  };

  const handleSubmit = async () => {
    setError('');
    setLoading(true);

    try {
      const headers = await getAuthHeaders(auth.getAccessTokenSilently);
      
      const response = await fetch(getApiUrl('/api/users/me/api-key'), {
        method: 'PUT',
        headers: headers
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail?.message || data.detail || 'Failed to generate token');
      }

      // Update token with the newly generated token from server
      const newToken = data.token || '';
      setToken(newToken);
      setError('');
      
      // Show success toast notification
      toaster.push(
        <Message type="success" showIcon>
          Token generated successfully!
        </Message>,
        { placement: 'bottomCenter', duration: 3000 }
      );
      
      // Refresh user data to get updated metadata
      if (auth.getUser) {
        try {
          await auth.getUser();
        } catch (err) {
          console.warn('Failed to refresh user data:', err);
        }
      }
    } catch (err) {
      console.error('Token generation error:', err);
      setError(err.message || 'Failed to generate token. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setError('');
      setToken('');
      onClose();
    }
  };

  return (
    <Modal open={show} onClose={handleClose} size="sm">
      <Modal.Header>
        <Modal.Title>Manage API Token</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {fetching ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <Loader size="md" content="Loading current token..." />
          </div>
        ) : (
          <Form fluid onSubmit={handleSubmit}>
            {error && (
              <Message type="error" showIcon style={{ marginBottom: 20 }}>
                {error}
              </Message>
            )}

            <Message type="info" showIcon style={{ marginBottom: 20 }}>
              <strong>API Token</strong> is used for API authentication. Click "Revoke Token" to generate a new token.
            </Message>
            
            <Form.Group>
              <Form.ControlLabel>Token</Form.ControlLabel>
              <InputGroup>
                <Input
                  value={token}
                  readOnly
                  placeholder="Enter your API token"
                  type="text"
                  disabled={loading}
                />
                <InputGroup.Button>
                  <CopyButton
                    text={token}
                    disabled={loading}
                    successMessage="Token copied to clipboard"
                    errorMessage="Failed to copy token to clipboard"
                  />
                </InputGroup.Button>
              </InputGroup>
            </Form.Group>

            <Form.Group>
              <Button 
                appearance="primary" 
                type="submit" 
                block 
                loading={loading}
                disabled={loading}
              >
                Revoke Token
              </Button>
            </Form.Group>
          </Form>
        )}
      </Modal.Body>
    </Modal>
  );
}

export default TokenDialog;

